import React, { useState, useRef, useEffect } from 'react';
import * as S from './AccountButton.style';
import { useStore } from '../../store/useStore';
import { createCheckout, getBillingPortal, logout } from '../../services/api';
import { trackEvent } from '../../services/analytics';

export const AccountButton: React.FC = () => {
  const { tier } = useStore();
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error';
  } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close popover on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only render for trial and active tiers
  if (tier !== 'trial' && tier !== 'active') {
    return null;
  }

  const handleSignOut = async () => {
    trackEvent('account_signout_click');
    try {
      await logout();
    } catch {
      // Best-effort logout
    }
    window.location.reload();
  };

  const handleCheckout = async () => {
    setLoading(true);
    setMessage(null);
    trackEvent('checkout_account_attempt');

    try {
      const { url } = await createCheckout();
      trackEvent('checkout_account_redirect');
      window.location.href = url;
    } catch (err) {
      trackEvent('checkout_account_error');
      setMessage({
        text: err instanceof Error ? err.message : 'Failed to create checkout session',
        type: 'error',
      });
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    trackEvent('billing_portal_open_attempt');
    try {
      const { url } = await getBillingPortal();
      trackEvent('billing_portal_open_redirect');
      window.location.href = url;
    } catch {
      trackEvent('billing_portal_open_error');
      // Fail silently - user will stay on page
    }
  };

  const openUpgradeModal = () => {
    trackEvent('account_upgrade_modal_open');
    setShowPopover(false);
    setShowModal(true);
    setMessage(null);
    setLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setMessage(null);
    setLoading(false);
  };

  return (
    <>
      <S.Wrapper ref={wrapperRef}>
        <S.AccountBtn
          onClick={() => {
            setShowPopover((previous) => {
              const next = !previous;
              if (next) {
                trackEvent('account_menu_open');
              }
              return next;
            });
          }}
        >
          [ account ]
        </S.AccountBtn>

        {showPopover && (
          <S.Popover onClick={(e) => e.stopPropagation()}>
            <S.TierLabel>tier: {tier === 'active' ? 'pro' : tier}</S.TierLabel>
            {tier === 'trial' && (
              <S.PopoverButton onClick={openUpgradeModal}>[ upgrade to pro ]</S.PopoverButton>
            )}
            {tier === 'active' && (
              <S.PopoverButton onClick={handleManageSubscription}>
                [ manage subscription ]
              </S.PopoverButton>
            )}
            <S.PopoverButton onClick={handleSignOut}>[ sign out ]</S.PopoverButton>
          </S.Popover>
        )}
      </S.Wrapper>

      {showModal && (
        <S.Overlay onClick={closeModal}>
          <S.Card onClick={(e) => e.stopPropagation()}>
            <S.Title>upgrade to pro</S.Title>
            <S.Description>
              You&apos;ll be redirected to Stripe to complete checkout. Once payment is confirmed,
              you&apos;ll be returned here with full live access.
            </S.Description>
            <S.ModalActions>
              <S.ModalButton onClick={handleCheckout} disabled={loading}>
                {loading ? '[ redirecting... ]' : '[ upgrade to pro ]'}
              </S.ModalButton>
              <S.ModalSecondaryButton onClick={closeModal}>[ cancel ]</S.ModalSecondaryButton>
              {message && <S.Message $type={message.type}>{message.text}</S.Message>}
            </S.ModalActions>
          </S.Card>
        </S.Overlay>
      )}
    </>
  );
};
